import { ReturnComponent } from '@/utils/reactTypes';
import { Box, BoxProps, SxProps, Theme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useMemo } from 'react';

// The metrics of the split chips the order rows use, so the two read as one family.
const PILL_SX = {
    px: 1,
    py: '2px',
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    borderRadius: 999,
};

interface ClinicianLicenseChipProps extends Omit<BoxProps, 'children'> {
    disciplineCodes: string[];
    licenses: string[];
    disciplineColors: Map<string, string>;
}

/* An outlined pill in the org's discipline color carrying the licenses a clinician holds.
 * Renders nothing for a clinician with no license on record.
 */
export const ClinicianLicenseChip = ({
    disciplineCodes,
    licenses,
    disciplineColors,
    sx: providedSx,
    ...rest
}: ClinicianLicenseChipProps): ReturnComponent => {
    // Colored by the first discipline, which is the one a multi-discipline clinician leads with.
    const color = useMemo(
        () => disciplineColors.get(disciplineCodes[0]) ?? grey[600],
        [disciplineColors, disciplineCodes],
    );

    const sx = useMemo(
        (): SxProps<Theme> => ({
            ...PILL_SX,
            border: `1px solid ${color}`,
            color,
            ...providedSx,
        }),
        [color, providedSx],
    );

    const label = useMemo(() => licenses.join(', '), [licenses]);

    if (licenses.length === 0) {
        return null;
    }

    return (
        <Box sx={sx} {...rest}>
            {label}
        </Box>
    );
};
